/**
 * Initialises and manages the dealer locator functionality.
 * IMPORTANT: To update the dealer data, look in 'us-canada-dealers.json' and 'international-dealers.json'.
 * which can be found in the assets folder.
 *
 * @param {string} usaDealers - URL to fetch the USA dealers data.
 * @param {string} internationalDealers - URL to fetch the international dealers data.
 * @returns {Object} An object containing methods and properties for the dealer locator.
 * @returns {boolean} IS_USA_CANADA - Indicates if the current shop is in the USA or Canada.
 * @returns {Array} USA_CANADA_DEALERS - List of dealers in the USA and Canada.
 * @returns {Array} INTERNATIONAL_DEALERS - List of international dealers.
 * @returns {Object} DEALERS - GeoJSON object containing all dealer features.
 * @returns {Object} MAP - Mapbox GL map instance.
 * @returns {boolean} USER_INTERACTING - Indicates if the user is interacting with the map.
 * @returns {Function} init - Initialises the dealer locator, loads dealer data, and sets up the map.
 */
const REGION_MAP = {
  "north-america": ["United States", "USA", "US", "Canada"],
  europe: ["UK", "Belgium", "France", "Germany", "Netherlands", "Norway", "Poland", "Polska", "Portugal", "RS", "HU", "Sweden"],
  asia: ["China", "Singapore", "Thailand", "UAE", "Vietnam"],
  australia: ["Australia", "New Zealand"],
  "south-africa": ["South Africa"],
};

const REGION_COORDS = {
  "north-america": { center: [-95, 40], zoom: 3 },
  europe: { center: [15, 54], zoom: 4 },
  asia: { center: [100, 20], zoom: 3 },
  australia: { center: [135, -25], zoom: 3 },
  "south-africa": { center: [25, -29], zoom: 5 },
};

function dealerLocator(usaDealers, internationalDealers) {
  const US_STATE_NAME_TO_ABBR = {
    ALABAMA: "AL",
    ALASKA: "AK",
    ARIZONA: "AZ",
    ARKANSAS: "AR",
    CALIFORNIA: "CA",
    COLORADO: "CO",
    CONNECTICUT: "CT",
    DELAWARE: "DE",
    FLORIDA: "FL",
    GEORGIA: "GA",
    HAWAII: "HI",
    IDAHO: "ID",
    ILLINOIS: "IL",
    INDIANA: "IN",
    IOWA: "IA",
    KANSAS: "KS",
    KENTUCKY: "KY",
    LOUISIANA: "LA",
    MAINE: "ME",
    MARYLAND: "MD",
    MASSACHUSETTS: "MA",
    MICHIGAN: "MI",
    MINNESOTA: "MN",
    MISSISSIPPI: "MS",
    MISSOURI: "MO",
    MONTANA: "MT",
    NEBRASKA: "NE",
    NEVADA: "NV",
    "NEW HAMPSHIRE": "NH",
    "NEW JERSEY": "NJ",
    "NEW MEXICO": "NM",
    "NEW YORK": "NY",
    "NORTH CAROLINA": "NC",
    "NORTH DAKOTA": "ND",
    OHIO: "OH",
    OKLAHOMA: "OK",
    OREGON: "OR",
    PENNSYLVANIA: "PA",
    "RHODE ISLAND": "RI",
    "SOUTH CAROLINA": "SC",
    "SOUTH DAKOTA": "SD",
    TENNESSEE: "TN",
    TEXAS: "TX",
    UTAH: "UT",
    VERMONT: "VT",
    VIRGINIA: "VA",
    WASHINGTON: "WA",
    "WEST VIRGINIA": "WV",
    WISCONSIN: "WI",
    WYOMING: "WY",
    "DISTRICT OF COLUMBIA": "DC",
  };

  function normaliseUSState(stateValue) {
    if (!stateValue) {
      return null;
    }

    const normalised = stateValue.trim().toUpperCase().replaceAll(".", "");
    if (US_STATE_NAME_TO_ABBR[normalised]) {
      return US_STATE_NAME_TO_ABBR[normalised];
    }

    const isKnownAbbreviation = Object.values(US_STATE_NAME_TO_ABBR).includes(
      normalised,
    );
    return isKnownAbbreviation ? normalised : null;
  }

  function isDealerInSearchedState(dealerState, searchedState) {
    const normalisedDealerState = normaliseUSState(dealerState);
    if (!normalisedDealerState || !searchedState) {
      return false;
    }

    return normalisedDealerState === searchedState;
  }

  function extractSearchedState(result) {
    const regionFromContext = result.context?.find((contextItem) =>
      contextItem.id?.startsWith("region"),
    );

    if (regionFromContext?.short_code) {
      return normaliseUSState(
        regionFromContext.short_code.split("-").at(-1) || null,
      );
    }

    if (regionFromContext?.text) {
      return normaliseUSState(regionFromContext.text);
    }

    const isRegionSearch =
      Array.isArray(result.place_type) && result.place_type.includes("region");

    if (isRegionSearch && result.short_code) {
      return normaliseUSState(result.short_code.split("-").at(-1) || null);
    }

    if (isRegionSearch && result.text) {
      return normaliseUSState(result.text);
    }

    return null;
  }

  // Spin the globe
  function spinGlobe(map, userInteracting) {
    const maxSpinZoom = 5;
    const slowSpinZoom = 3;
    const secondsPerRevolution = 120;

    const zoom = map.getZoom();
    if (!userInteracting && zoom < maxSpinZoom) {
      let distancePerSecond = 360 / secondsPerRevolution;
      if (zoom > slowSpinZoom) {
        // Slow spinning at higher zooms
        const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
        distancePerSecond *= zoomDif;
      }
      const center = map.getCenter();
      center.lng -= distancePerSecond;
      // Smoothly animate the map over one second.
      // When this animation is complete, it calls a 'moveend' event.
      map.easeTo({ center, duration: 1000, easing: (n) => n });
    }
  }

  // Function to handle flyover to dealer
  function flyToDealer(map, currentFeature) {
    map.flyTo({
      center: currentFeature.geometry.coordinates,
      zoom: 14,
    });
  }

  // Function to handle flyover to location
  function flyToLocation(map, lat, long, zoom = 6) {
    map.flyTo({
      center: [long, lat],
      zoom,
    });
  }

  // Create a pop up
  function createPopUp(map, currentFeature) {
    const popUps = document.querySelector(".mapboxgl-popup");
    // Check if there is already a popup on the map and if so, remove it
    if (popUps) {
      popUps.remove();
    }

    const { geometry, properties } = currentFeature;

    const popup = new mapboxgl.Popup({
      closeOnClick: true,
      focusAfterOpen: false,
      offset: 10,
    })
      .setLngLat(geometry.coordinates)
      .setHTML(
        `<p class="copy-xsm-light" style="min-width: 20rem">
        <strong class="copy-xsm-medium">${properties.name}</strong><br />
        ${properties.address},<br />
        ${properties.address2 ? `${properties.address2}, ` : ""}
        ${properties.city}<br />
        ${properties.state}, ${properties.postal}<br />
        ${properties.country}<br />
        <br />
        <a href="https://www.${properties.web}" target="_blank">${
          properties.web
        }</a><br />
        <a href="tel:${properties.phone}">${properties.phoneFormatted}</a>
        ${
          properties.distance
            ? `<br /><span class="copy-xsm-medium">${properties.distance.toFixed(
                2,
              )} miles away</span>`
            : ""
        }
      </p>`,
      )
      .addTo(map);
  }

  // Create a list of dealers dynamically using GeoJSON data
  function buildDealersList(map, dealers, searchContext = {}) {
    const searchedState = searchContext.searchedState || null;
    const within100 = dealers.features.filter((dealer) => {
      const dealerDistance = dealer.properties.distance;
      return Number.isFinite(dealerDistance) && dealerDistance <= 100;
    });
    const sortedDistances = dealers.features
      .map((dealer) => dealer.properties.distance)
      .filter((distance) => Number.isFinite(distance))
      .sort((a, b) => a - b);
    const closestDistance = sortedDistances.length ? sortedDistances[0] : null;
    const shouldEnforceDistanceLimit =
      within100.length >= 3 ||
      (closestDistance !== null && closestDistance > 500);

    const hasStateMatches = searchedState
      ? dealers.features.some((dealer) =>
          isDealerInSearchedState(dealer.properties.state, searchedState),
        )
      : false;

    for (const dealer of dealers.features) {
      const listings = document.querySelector(".dealers__sidebar__listings");
      const listing = listings.appendChild(document.createElement("div"));

      const { properties } = dealer;

      listing.id = `listing-${properties.id}`;
      listing.className = `dealers__listings__item`;

      const link = listing.appendChild(document.createElement("div"));
      link.className = "dealers__listings__link";
      link.id = `link-${properties.id}`;
      link.innerHTML = `<button type="button" class="copy-medium" title="View on map">${properties.name}</button>`;

      // Set the distance from the searched location
      const distance = Number.isFinite(properties.distance)
        ? Math.round(100 * properties.distance) / 100
        : null;

      const isStateMatch = isDealerInSearchedState(
        properties.state,
        searchedState,
      );

      // If the user searched for a US state and we found state matches, only show those.
      const hideForStateSearch =
        searchedState && hasStateMatches && !isStateMatch;

      // If there are 3 or more results within 100 miles, hide very distant results,
      // except when the dealer is in the searched state.
      const hideForDistance =
        shouldEnforceDistanceLimit &&
        distance !== null &&
        distance > 500 &&
        !isStateMatch;

      if (hideForStateSearch || hideForDistance) {
        listing.style.display = "none";
      }

      const pinIcon = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
        </svg>
      `;
      const phoneIcon = `
        <svg viewBox="0 0 500 500" aria-hidden="true" focusable="false">
          <path d="M429.47,354.85l-54.23-54.23c-5.74-5.75-13.37-8.9-21.48-8.9s-15.74,3.15-21.48,8.89l-11.21,11.21c-4.44,4.45-11.1,5.91-16.96,3.73-14.09-5.23-42.13-18.4-71.65-47.92-28.54-28.54-41.61-55.41-47.18-69.91-.21-3.97-.28-7.92-.08-11.89.02-.31-.17-.6-.17-.91.79-2.1,1.58-4.25,3.23-5.91l11.21-11.21c5.74-5.73,8.9-13.36,8.9-21.48s-3.16-15.74-8.9-21.48l-54.23-54.24c-9.44-9.43-24.24-11.59-35.98-5.21l-20.04,10.88c-16.5,8.95-27.02,26.15-27.47,44.88-1.08,45.84,9.94,135.55,95.79,221.4,82.41,82.41,169.09,95.71,216.37,95.71,1.68,0,3.32-.01,4.9-.05,18.81-.37,36.08-10.89,45.08-27.48l10.81-19.9h0c6.37-11.73,4.22-26.53-5.22-35.98ZM187.72,357.56s0,.03,0,.05c-.05-.04-.09-.08-.13-.11.04.01.08.04.12.05Z" fill="currentColor"/>
        </svg>
      `;
      const globeIcon = `
        <svg viewBox="0 0 500 500" aria-hidden="true" focusable="false">
          <path d="M250,57.32c-51.47,0-99.85,20.04-136.25,56.44-36.39,36.39-56.44,84.78-56.44,136.25s20.04,99.85,56.44,136.25c36.39,36.39,84.78,56.44,136.25,56.44s99.85-20.04,136.25-56.44c36.39-36.39,56.44-84.78,56.44-136.25s-20.04-99.85-56.44-136.25c-36.39-36.39-84.78-56.44-136.25-56.44ZM379.34,120.66c2.95,2.95,5.78,5.99,8.5,9.1-15.79,7.19-31.98,13.09-48.51,17.68-3.98-11.62-8.66-22.53-14.01-32.53-10.19-19.05-22.19-33.7-35.35-43.46,33.6,7.45,64.44,24.29,89.36,49.21ZM254.88,169.24c26.61-.36,52.67-3.72,78-10.01,7.85,25.97,12.21,55.29,12.63,85.89h-90.63v-75.88ZM254.88,159.47v-92.14c23.14,2.27,44.93,20.59,61.83,52.18,5,9.35,9.39,19.55,13.15,30.41-24.35,5.99-49.4,9.19-74.98,9.55ZM245.12,159.47c-25.58-.36-50.63-3.56-74.98-9.55,3.76-10.86,8.15-21.05,13.15-30.41,16.9-31.59,38.69-49.92,61.83-52.18v92.14ZM245.12,169.24v75.88h-90.63c.42-30.6,4.78-59.91,12.63-85.89,25.33,6.29,51.39,9.64,78,10.01ZM144.72,245.12h-77.57c1.03-39.48,14.5-76.85,38.56-107.58,16.9,7.85,34.25,14.26,51.97,19.21-8.06,26.82-12.54,56.97-12.96,88.38ZM144.72,254.88c.42,31.4,4.9,61.56,12.96,88.37-17.72,4.95-35.07,11.35-51.97,19.2-24.05-30.73-37.53-68.1-38.56-107.58h77.57ZM154.48,254.88h90.63v75.88c-26.61.36-52.67,3.72-78,10.01-7.85-25.97-12.21-55.29-12.63-85.89ZM245.12,340.53v92.14c-23.14-2.27-44.93-20.59-61.83-52.18-5-9.35-9.39-19.55-13.15-30.41,24.35-5.99,49.4-9.19,74.98-9.55ZM254.88,340.53c25.58.36,50.63,3.56,74.98,9.55-3.76,10.86-8.15,21.05-13.15,30.41-16.9,31.59-38.69,49.92-61.83,52.18v-92.14ZM254.88,330.76v-75.88h90.63c-.42,30.6-4.78,59.91-12.63,85.89-25.33-6.29-51.39-9.64-78-10.01ZM355.28,254.88h77.57c-1.03,39.48-14.5,76.85-38.56,107.58-16.9-7.85-34.25-14.26-51.97-19.21,8.06-26.82,12.54-56.97,12.96-88.38ZM355.28,245.12c-.42-31.4-4.9-61.56-12.96-88.37,17.72-4.95,35.07-11.35,51.97-19.2,24.05,30.73,37.53,68.1,38.56,107.58h-77.57ZM120.66,120.66c24.92-24.92,55.77-41.76,89.36-49.21-13.15,9.76-25.16,24.41-35.35,43.46-5.35,10-10.03,20.91-14.01,32.53-16.53-4.59-32.71-10.49-48.5-17.68,2.72-3.11,5.55-6.14,8.49-9.09ZM120.66,379.34c-2.95-2.95-5.78-5.99-8.5-9.1,15.79-7.19,31.98-13.09,48.51-17.68,3.98,11.62,8.66,22.53,14.01,32.53,10.19,19.05,22.19,33.7,35.35,43.46-33.59-7.45-64.44-24.29-89.36-49.21ZM379.34,379.34c-24.92,24.92-55.77,41.76-89.36,49.21,13.15-9.76,25.16-24.41,35.35-43.46,5.35-10,10.03-20.91,14.01-32.53,16.53,4.59,32.71,10.49,48.5,17.68-2.72,3.11-5.54,6.14-8.49,9.09Z" fill="currentColor"/>
        </svg>
      `;

      const meta = document.createElement("div");
      meta.className = "dealer__listings__link__meta copy-light";

      const addressLine = properties.address?.trim();
      const addressDetailsLine = [
        properties.address2,
        properties.city,
        properties.state,
        properties.postal,
        properties.country,
      ]
        .filter((part) => part?.trim()?.length)
        .join(", ");

      if (addressLine || addressDetailsLine) {
        meta.innerHTML += `
          <div class="dealer-location-meta__row dealer-location-meta__row--address">
            <span class="dealer-location-meta__icon">${pinIcon}</span>
            <div class="dealer-location-meta__text">
              ${
                addressLine
                  ? `<span class="dealer-location-meta__line">${addressLine}</span>`
                  : ""
              }
              ${
                addressDetailsLine
                  ? `<span class="dealer-location-meta__line">${addressDetailsLine}</span>`
                  : ""
              }
            </div>
          </div>
        `;
      }
      if (properties.phone?.length > 4) {
        // The > 4 ensures the phone number is truly empty even if the dialling code is dynamically added
        meta.innerHTML += `
          <div class="dealer-location-meta__row">
            <span class="dealer-location-meta__icon">${phoneIcon}</span>
            <a href="tel:${properties.phone}" class="dealer-location-meta__phone">${properties.phoneFormatted}</a>
          </div>
        `;
      }
      if (properties.web) {
        meta.innerHTML += `
          <div class="dealer-location-meta__row">
            <span class="dealer-location-meta__icon">${globeIcon}</span>
            <a href="https://www.${properties.web}" target="_blank" class="dealer-location-meta__web">${properties.web}</a>
          </div>
        `;
      }
      if (distance) {
        meta.innerHTML += `<p class="copy-xsm-medium">${distance.toFixed(
          2,
        )} miles away</p>`;
      }

      link.appendChild(meta);

      const linkTitle = link.querySelector("button");
      linkTitle.addEventListener("click", (e) => {
        for (const feature of dealers.features) {
          if (e.target.parentNode.id === `link-${feature.properties.id}`) {
            flyToDealer(map, feature);
            createPopUp(map, feature);
          }
        }

        const activeItem = document.querySelector(
          ".dealers__sidebar__listings--active",
        );
        activeItem?.classList?.remove("dealers__sidebar__listings--active");
        e.target
          .closest(".dealers__listings__item")
          .classList.add("dealers__sidebar__listings--active");
      });
    }
  }

  // Show the searched location and closest store
  function getBbox(sortedDealers, dealerIdentifier, searchResult) {
    const lats = [
      sortedDealers.features[dealerIdentifier].geometry.coordinates[1],
      searchResult.coordinates[1],
    ];
    const lons = [
      sortedDealers.features[dealerIdentifier].geometry.coordinates[0],
      searchResult.coordinates[0],
    ];
    const sortedLons = lons.sort((a, b) => {
      if (a > b) {
        return 1;
      }
      if (a.distance < b.distance) {
        return -1;
      }
      return 0;
    });
    const sortedLats = lats.sort((a, b) => {
      if (a > b) {
        return 1;
      }
      if (a.distance < b.distance) {
        return -1;
      }
      return 0;
    });
    return [
      [sortedLons[0], sortedLats[0]],
      [sortedLons[1], sortedLats[1]],
    ];
  }

  function loadDealers() {
    return Promise.all([
      fetch(usaDealers).then((response) => response.json()),
      fetch(internationalDealers).then((response) => response.json()),
    ]).then(([usaDealersData, internationalDealersData]) => {
      return {
        usaDealers: usaDealersData,
        internationalDealers: internationalDealersData,
      };
    });
  }

  return {
    SHOW_USA_CANADA: false,
    USA_CANADA_DEALERS: [],
    INTERNATIONAL_DEALERS: [],
    DEALERS: null,
    MAP: null,
    USER_INTERACTING: false,

    async init() {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      if (
        params.get("sa") === "true" ||
        Boolean(window.Shopify.shop.includes("usa"))
      ) {
        this.SHOW_USA_CANADA = true;
      }

      try {
        const dealers = await loadDealers().then((dealers) => {
          if (this.SHOW_USA_CANADA) {
            this.USA_CANADA_DEALERS = dealers.usaDealers;
            this.INTERNATIONAL_DEALERS = dealers.internationalDealers;
          } else {
            this.USA_CANADA_DEALERS = [];
            this.INTERNATIONAL_DEALERS = dealers.internationalDealers;
          }
        });

        mapboxgl.accessToken =
          "pk.eyJ1Ijoic3R1ZGlvZG0iLCJhIjoiY2lxa3RvYjlxMDAyNGh3bThybjBvdXIwbyJ9.ITW737Xzho3njsQInRTCXg";

        this.MAP = new mapboxgl.Map({
          container: "dealers-map",
          style: "mapbox://styles/mapbox/light-v11",
          center: [1.8904, 52.4862],
          zoom: 3,
          scrollZoom: true,
        });

        this.DEALERS = {
          type: "FeatureCollection",
          features: [...this.USA_CANADA_DEALERS, ...this.INTERNATIONAL_DEALERS],
        };

        this.DEALERS.features.sort((a, b) => {
          const nameA = a.properties.name.toUpperCase();
          const nameB = b.properties.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });

        // Manipulate the dealer features data
        this.DEALERS.features.forEach((dealer, i) => {
          // Add a unique ID to each dealer as a property
          dealer.properties.id = i;

          // Add dialling codes to 'United States' and 'Canada' countries
          // Only when on the non-US/CA site
          if (
            !this.IS_USA_CANADA &&
            (dealer.properties.country === "United States" ||
              dealer.properties.country === "Canada")
          ) {
            dealer.properties.phone = `001${dealer.properties.phone}`;
          }
        });

        // Create an image for the map marker symbol
        this.MAP.loadImage(
          "https://cdn.shopify.com/s/files/1/2054/5999/files/map-marker-dark.png?v=1711372752",
          (error, image) => {
            if (error) throw error;
            this.MAP.addImage("dealer-marker", image, { sdf: true });
          },
        );

        this.MAP.on("load", () => {
          // Add each dealer as a point on the map using a named source
          this.MAP.addSource("dealers-source", {
            type: "geojson",
            data: this.DEALERS,
          });

          this.MAP.addLayer({
            id: "locations",
            type: "symbol",
            source: "dealers-source",
            layout: {
              "icon-image": "dealer-marker",
              "icon-size": 0.75,
            },
            paint: {
              "icon-color": "#343838",
            },
          });

          // Build a list of dealers in the sidebar
          buildDealersList(this.MAP, this.DEALERS);

          // Wire up region filter buttons
          const filterBtns = document.querySelectorAll(".dealers__filter__btn");
          filterBtns.forEach((btn) => {
            btn.addEventListener("click", () => {
              filterBtns.forEach((b) =>
                b.classList.remove("dealers__filter__btn--active"),
              );
              btn.classList.add("dealers__filter__btn--active");
              this.filterDealers(btn.dataset.region);
            });
          });
        });

        // Set land colour on style load to differentiate from white sidebar
        this.MAP.on("style.load", () => {
          if (this.MAP.getLayer("land")) {
            this.MAP.setPaintProperty("land", "background-color", "#f9f9f9");
          }
        });

        // Variable to store the search container
        const searchContainer = document.querySelector(".dealers__search");

        // Create the MapboxGeocoder
        const geocoder = new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl: mapboxgl,
          placeholder: "Search by location or Zip / Postcode",
        });

        // Append the geocoder to the search container
        searchContainer.appendChild(geocoder.onAdd(this.MAP));

        // When a result is returned from the geocoder...
        geocoder.on("result", (event) => {
          // Get the search result
          const searchResult = event.result.geometry;
          const options = { units: "miles" };
          for (const dealer of this.DEALERS.features) {
            dealer.properties.distance = turf.distance(
              searchResult,
              dealer.geometry,
              options,
            );
          }

          // Sort the dealers by distance
          this.DEALERS.features.sort((a, b) => {
            if (a.properties.distance > b.properties.distance) {
              return 1;
            }
            if (a.properties.distance < b.properties.distance) {
              return -1;
            }
            return 0;
          });

          // Remove the existing listings
          const listings = document.querySelector(
            ".dealers__sidebar__listings",
          );
          while (listings.firstChild) {
            listings.removeChild(listings.firstChild);
          }

          // Build a new list of dealers
          buildDealersList(this.MAP, this.DEALERS, {
            searchedState: extractSearchedState(event.result),
          });

          // Apply an 'active' class to the closest dealer
          const activeListing = document.getElementById(
            `listing-${this.DEALERS.features[0].properties.id}`,
          );
          activeListing.classList.add("dealers__sidebar__listings--active");

          // Update map view to show both the search result and the closest dealer
          const bbox = getBbox(dealers, 0, searchResult);
          map.fitBounds(bbox, {
            padding: 100,
          });

          createPopUp(this.MAP, this.DEALERS.features[0]);
        });

        // Function to handle clicking the location links below the map
        const locationLink = document.querySelectorAll(".location-entry");
        locationLink.forEach((link) => {
          link.addEventListener("click", () => {
            // Fly to the location
            flyToLocation(
              this.MAP,
              parseFloat(link.dataset.lat),
              parseFloat(link.dataset.lng),
            );

            // Perform a search for the location
            const location = link.innerText;
            const region = link.closest(".location-column").id;
            geocoder.setInput(`${location}, ${region}`);

            // Scroll up to the map
            const mapContainer = document.querySelector(".dealers");
            mapContainer.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });

            // Trigger the search
            const searchInput = document.querySelector(
              ".mapboxgl-ctrl-geocoder--input",
            );
            setTimeout(() => {
              // Set the cursor on the input as active
              searchInput.focus();

              // fake an 'enter' keypress to trigger the search
              const enterEvent = new KeyboardEvent("keydown", {
                key: "Enter",
                code: "Enter",
                keyCode: 13,
                charCode: 13,
                bubbles: true,
              });
              searchInput.dispatchEvent(enterEvent);
            }, 250);
          });
        });

        // Enable globe spinning
        spinGlobe(this.MAP, this.USER_INTERACTING);

        this.MAP.on("moveend", () => {
          spinGlobe(this.MAP, this.USER_INTERACTING);
        });

        this.MAP.on("mouseenter", "locations", (e) => {
          createPopUp(this.MAP, e.features[0]);
          this.MAP.getCanvas().style.cursor = "pointer";
        });

        this.MAP.on("mouseleave", "locations", () => {
          this.MAP.getCanvas().style.cursor = "";
        });

        this.MAP.on("mousedown", () => {
          this.USER_INTERACTING = true;
        });

        this.MAP.on("dragstart", () => {
          this.USER_INTERACTING = true;
        });
        this.MAP.on("zoomstart", () => {
          this.USER_INTERACTING = true;
        });
      } catch (error) {
        console.error("Error loading dealers:", error);
      }
    },

    filterDealers(region) {
      let filtered;
      if (region === "all") {
        filtered = this.DEALERS.features;
        this.MAP.flyTo({ center: [1.8904, 52.4862], zoom: 3 });
      } else {
        const countries = REGION_MAP[region] || [];
        filtered = this.DEALERS.features.filter((f) =>
          countries.includes(f.properties.country),
        );
        const coords = REGION_COORDS[region];
        if (coords) {
          this.MAP.flyTo({ center: coords.center, zoom: coords.zoom });
        }
      }

      // Update map markers
      this.MAP.getSource("dealers-source").setData({
        type: "FeatureCollection",
        features: filtered,
      });

      // Rebuild sidebar list
      const listings = document.querySelector(".dealers__sidebar__listings");
      while (listings.firstChild) {
        listings.removeChild(listings.firstChild);
      }
      buildDealersList(this.MAP, {
        type: "FeatureCollection",
        features: filtered,
      });
    },
  };
}
